import { toGqlId } from "@src/api/formatters/shopify";
import { createAsyncThunk } from "@src/store/createAsyncThunk";
import { getVariationDraft } from "@src/store/slices/experienceBuilder/component/selectors/getVariationDraft";
import type { OnsiteUpsellComponent } from "@src/store/slices/experienceBuilder/types";
import {
  openProductPicker,
  PRODUCT_PICKER_VARIANTS_PAGE_SIZE,
  setProductPickerProducts,
  setProductPickerSelectedProducts,
  setProductPickerSelectedProductsLoading,
} from "@src/store/slices/productPicker";
import { loadProductsWithVariants } from "@src/store/slices/productPicker/util/loadProductsWithVariants";
import { fetchedToSelectedProducts } from "@src/store/slices/productPicker/util/upsellSelection";

// Cart "contains variants": open the variant picker seeded from the condition's
// {productId, variantId} pairs. The flat rule stores ids only, so fetch each
// product with its variants so the picker shows them when reopening a saved
// experience (and the save step can reconcile variant selection).
export const openOnsiteUpsellCartVariantsProductPicker = createAsyncThunk<
  void,
  { ruleIndex: number; conditionIndex: number }
>(
  "productPicker/openOnsiteUpsellCartVariantsProductPicker",
  async ({ ruleIndex, conditionIndex }, { dispatch, getState }) => {
    dispatch(
      openProductPicker({
        type: "onsiteUpsellCartVariants",
        ruleIndex,
        conditionIndex,
      })
    );

    const draft = getVariationDraft(getState()) as
      | OnsiteUpsellComponent
      | undefined;
    const condition = draft?.rules[ruleIndex]?.conditions[conditionIndex];
    const variants = condition?.type === "variants" ? condition.variants : [];
    if (variants.length === 0) {
      dispatch(setProductPickerSelectedProducts([]));
      return;
    }

    // Group the stored variant ids (as gids, matching the grid) by product.
    const variantIdsByProduct = new Map<string, string[]>();
    for (const { productId, variantId } of variants) {
      const list = variantIdsByProduct.get(productId) ?? [];
      list.push(toGqlId(variantId, "ProductVariant"));
      variantIdsByProduct.set(productId, list);
    }
    const productIds = [...variantIdsByProduct.keys()];

    dispatch(setProductPickerSelectedProductsLoading(true));
    const { data: products } = await loadProductsWithVariants({
      first: productIds.length,
      query: productIds.map((id) => `id:${id}`).join(" OR "),
      variantsFirst: PRODUCT_PICKER_VARIANTS_PAGE_SIZE,
    });
    // Seed the grid so variant options render before the live load reconciles.
    dispatch(setProductPickerProducts(products));

    const selectedProducts = fetchedToSelectedProducts(
      products,
      productIds,
      (product, id) => ({
        selectedVariantIds: variantIdsByProduct.get(id),
        totalVariantCount: product?.variants?.length,
      })
    );
    dispatch(setProductPickerSelectedProducts(selectedProducts));
    dispatch(setProductPickerSelectedProductsLoading(false));
  }
);
